import React, { useState, useMemo } from 'react';

// Custom hook for pagination logic
export const usePagination = (data, itemsPerPage = 10) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate pagination values
  const paginationData = useMemo(() => {
    const totalItems = data.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);

    return {
      currentItems,
      totalPages,
      totalItems,
      indexOfFirstItem,
      indexOfLastItem,
    };
  }, [data, currentPage, itemsPerPage]);

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    console.log("Page changed to:", pageNumber);
    setCurrentPage(pageNumber);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      console.log("Previous page")
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < paginationData.totalPages) {
      console.log ("Next page")
      setCurrentPage(currentPage + 1);
    }
  };

  // Reset to first page (useful after filtering, sorting, or deleting)
  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  return {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    ...paginationData,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
    resetToFirstPage,
  };
};

// Pagination Component
const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onPreviousPage,
  onNextPage,
  showInfo = true,
  className = "",
  itemName = "items", // customizable item name (e.g., "categories", "products", etc.)
  hideControls = false // <-- added prop to hide controls
}) => {
  // Calculate display info
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  // Don't render if there are no items
  if (totalItems === 0) {
    return null;
  }

  return (
    <div className={`bg-white ${className}`}>
      {/* Items Count and Pagination Info */}
      {showInfo && (
        <div className="px-4 py-2  border-b flex justify-between items-center text-sm text-gray-600">
          <div className="font-medium">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalItems)} of {totalItems} {itemName}
          </div>
          <div className="font-medium">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      )}

      {/* Pagination Controls - only show if more than one page and not hidden */}
   {!hideControls && totalPages > 1 && (
  <div className="flex-shrink-0 sticky bottom-0 bg-white border-t shadow-sm z-10">
    <div className="flex items-center justify-between w-full px-4 py-3">
      {/* LEFT: Previous, Numbers, Next */}
      <div className="flex items-center space-x-2">
        <button
          onClick={onPreviousPage}
          disabled={currentPage === 1}
          className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>

        <div className="flex space-x-1">
          {getPageNumbers().map((pageNumber, index) => (
            <button
              key={index}
              onClick={() => typeof pageNumber === 'number' && onPageChange(pageNumber)}
              disabled={pageNumber === '...'}
              className={`px-3 py-2 text-sm border rounded-md transition-colors ${
                pageNumber === currentPage
                  ? 'bg-blue-500 text-white border-blue-500 shadow-sm'
                  : pageNumber === '...'
                  ? 'cursor-default border-gray-300 text-gray-400'
                  : 'border-gray-300 hover:bg-gray-50 text-gray-700'
              }`}
            >
              {pageNumber}
            </button>
          ))}
        </div>

        <button
          onClick={onNextPage}
          disabled={currentPage === totalPages}
          className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Next
        </button>
      </div>

      {/* RIGHT: Total Items */}
      <div className="text-sm text-gray-600 font-medium whitespace-nowrap">
        Total: {totalItems} {itemName}
      </div>
    </div>
  </div>
)}

    </div>
  );
};


export default Pagination;